import { Dispatch, SetStateAction } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Button } from "./ui/button";
import { IconX } from "@tabler/icons-react";
import { Input } from "./ui/input";
import z from "zod";
import { useSelector } from "react-redux";
import { userSelector } from "@/redux/user/user.selector";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { api } from "@/services/instance";
import { ErrorToast } from "@/utilities/toasts/ErrorToast";
import { SuccessToast } from "@/utilities/toasts/SuccessToast";
interface DialogProfileProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}
const formSchema = z.object({
  email: z.string().email({ message: "Ingresa un correo valido" }),
});

export const DialogRequest = ({ open, setOpen }: DialogProfileProps) => {
  const { loading } = useSelector(userSelector).ui;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleSendEmail = async (email: string) => {
    try {
      await api.post("/user/request-restore", { email });
      SuccessToast({
        message:
          "El correo ha sido enviado, favor de revisar tu bandeja de entrada",
      });
      form.reset();
      setOpen(false);
    } catch {
      ErrorToast({
        message:
          "No existe este usuario, favor de revisar con el administrador",
      });
      form.reset();
      setOpen(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <VisuallyHidden>
        <DialogTitle>RequestDialog</DialogTitle>
      </VisuallyHidden>
      <DialogContent
        className="bg-sidebar min-w-[15rem] w-[30rem]"
        aria-describedby={undefined}
      >
        <DialogHeader className="flex pb-0 h-fit gap-1">
          <Box className="flex justify-end w-full font-bold">
            <Button
              className="p-0 text-[#1F6B45] h-fit"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <IconX className="p-0" />
            </Button>
          </Box>
          <DialogTitle>
            <Typography
              fontFamily={"Inter"}
              fontSize={"18px"}
              fontWeight={500}
              letterSpacing={0}
            >
              Recuperar contraseña
            </Typography>
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Ingresa el correo electrónico asociado a tu cuenta. Te enviaremos un
            enlace para restablecer tu contraseña.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(({ email }) => {
              handleSendEmail(email);
            })}
            className="space-y-4 flex flex-col justify-between"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold text-[14px]">
                    Ingresa tu correo electrónico
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Ingresa tu correo"
                      {...field}
                      className="text-[14px] text-[#1F6B45] max-w-[100%] min-w-[100%]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter
              className={`flex w-full gap-4 pt-4 flex-row items-center justify-center flex-wrap sm:flex-row sm:justify-end sm:items-center`}
            >
              <Button
                variant="contained"
                className="w-full justify-self-end w-[7.5rem] flex hover:bg-[#1F6B45]/90 shadow-sm"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <CircularProgress size={16} color="inherit" />
                  </div>
                ) : (
                  <span>Enviar solicitud</span>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
